import React from 'react'
import { SelectCard } from 'src/Components/Ui/SelectCard'
import { State, PartialAction, Position } from 'src/types'
import { TabPanel } from 'src/Components/Ui/TabPanel'
import { SelectCardWithImage } from 'src/Components/Ui/SelectCardWithImage'
import { useDispatcher } from 'src/features/atoms/glove/us/logic/dispatchGenerator'
import { useLocale } from 'src/locales/useLocale'
import {
  atomsFlagGenerator,
  baseCautionGenerator,
  fingerGuardTypeObjectSwitcher,
  webPartsFilter
} from 'src/features/atoms/glove/us/logic/baseSetting'
import { positionChecker } from 'src/logic'
import { webPartsObjs } from 'src/features/atoms/glove/constants/bases'

type Props = {
  state: State
  selectedIndex: number
  position: Position
  dispatch: React.Dispatch<PartialAction>
}

export const BaseSetter: React.FC<Props> = ({ state, selectedIndex, position, dispatch }) => {
  const { tanner, dominantArm, size, backLace, materialPack, fingerGuardType, webParts } = state
  const { t } = useLocale()
  const handle = {
    tanner: useDispatcher('tanner', dispatch, t),
    dominantArm: useDispatcher('dominantArm', dispatch, t),
    size: useDispatcher('size', dispatch, t),
    backLace: useDispatcher('backLace', dispatch, t),
    materialPack: useDispatcher('materialPack', dispatch, t),
    webParts: useDispatcher('webParts', dispatch, t),
    fingerGuardType: useDispatcher('fingerGuardType', dispatch, t),
    MoldingOrder: useDispatcher('moldingOrder', dispatch, t)
  }
  const { isTannerSelected, isDefaultSize, isLeftThrow, isMoldingOrder } = atomsFlagGenerator(state, position)

  // MEMO: positionCheckerはBaseSetterでのみ使用する。ColorSetterでは使用しないことに注意。 --- IGNORE ---
  const { isPitcher, isOutfielder } = positionChecker(state.baseModel.position)
  return (
    <TabPanel selectedIndex={selectedIndex} index={0}>
      <SelectCard
        summary={t.ATOMS_LEATHER}
        selectedLabel={tanner?.label}
        objects={t.TANNER_OBJECTS_US}
        handleChange={handle.tanner}
        caution={baseCautionGenerator('tanner', t)}
        disabled={isTannerSelected}
        isDisplay={!!state.kinds?.value}
        isError={!isTannerSelected}
      />
      {isTannerSelected && (
        <>
          <SelectCard
            summary={t.DOMINANT_ARM}
            selectedLabel={dominantArm.label}
            objects={t.DOMINANT_ARM_OBJECTS}
            handleChange={handle.dominantArm}
            isError={dominantArm?.value === 'unselected'}
            description={isLeftThrow && baseCautionGenerator('dominantArm', t)}
          />
          <SelectCard
            summary={t.MATERIAL_PACK}
            selectedLabel={materialPack?.label}
            objects={t.MATERIAL_PACK_OBJECTS}
            handleChange={handle.materialPack}
            isDisplay={isPitcher}
            isError={materialPack?.value === 'unselected'}
          />
          <SelectCard
            summary={t.FINGER_SIZE}
            selectedLabel={size.label}
            objects={t.SIZE_OBJECTS}
            handleChange={handle.size}
            caution={!isDefaultSize && baseCautionGenerator('size', t)}
            isError={size?.value === 'unselected'}
          />
          <SelectCard
            summary={t.BACK_LACE}
            selectedLabel={backLace?.label}
            objects={t.BACK_LACE_OBJECTS}
            handleChange={handle.backLace}
            isError={backLace?.value === 'unselected'}
          />
          <SelectCardWithImage
            summary={t.WEB}
            selectedLabel={webParts?.label}
            objects={webPartsFilter(state?.baseModel.productNumber, position, webPartsObjs)}
            handleChange={handle.webParts}
            isError={webParts?.value === 'unselected'}
          />
          <SelectCard
            summary={t.FINGER_GUARD}
            selectedLabel={fingerGuardType?.label}
            objects={fingerGuardTypeObjectSwitcher(isOutfielder, t)}
            handleChange={handle.fingerGuardType}
            isError={fingerGuardType?.value === 'unselected'}
          />
          <SelectCard
            summary={t.MOLDING_ORDER}
            selectedLabel={state?.moldingOrder?.label}
            objects={t.MOLDING_OBJECTS}
            handleChange={handle.MoldingOrder}
            isError={state?.moldingOrder?.value === 'unselected'}
            caution={isMoldingOrder && baseCautionGenerator('moldingOrder', t)}
          />
        </>
      )}
    </TabPanel>
  )
}
